import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { IProfession } from '@d-orca/shared/trusstor';
import type { IAdminTag } from '@interfaces/IAdminTag';
import { ChevronDownIcon } from '@/utils/icons';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { TradePill } from '@components/ui/TradePill/TradePill';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { tradeLabel } from '@utils/tradeLabel';
import {
  getManagementOptions,
  getProfessionFilterOptions,
  type IAdminPanelFilters,
} from './utils/tagFilters';
import styles from './styles.module.css';

interface IInventoryFilterSidebarProps {
  tags: IAdminTag[];
  professions: IProfession[];
  filters: IAdminPanelFilters;
  onChange: (filters: IAdminPanelFilters) => void;
  onClear: () => void;
}

interface ICheckRow {
  id: string;
  label: string;
  /** Custom content replacing the plain text label (e.g. a profession pill). */
  node?: ReactNode;
  /** Trailing count shown right-aligned. */
  count?: number;
  checked: boolean;
  onToggle: () => void;
}

/** Collapsible filter group — header shows the name plus the number of
 *  available options in parentheses, mirroring trusstor's ExpandableComponent. */
function FilterGroup({ title, rows }: { title: string; rows: ICheckRow[] }) {
  const [open, setOpen] = useState(true);
  if (rows.length === 0) return null;
  return (
    <div className={styles.filterGroup}>
      <button
        type="button"
        className={styles.filterHeader}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className={`${styles.filterChevron} ${open ? styles.filterChevronOpen : ''}`}>
          <ChevronDownIcon size={14} />
        </span>
        <span className={styles.filterTitle}>
          {title} <span className={styles.filterCount}>({rows.length})</span>
        </span>
      </button>
      {open && (
        <div className={styles.filterRows}>
          {rows.map((row) => (
            <label key={row.id} className={styles.checkRow}>
              <input type="checkbox" checked={row.checked} onChange={row.onToggle} />
              {row.node ?? <span>{row.label}</span>}
              {row.count !== undefined && <span className={styles.filterRowCount}>{row.count}</span>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function InventoryFilterSidebar({ tags, professions, filters, onChange, onClear }: IInventoryFilterSidebarProps) {
  const { t } = useTranslation();

  const professionOptions = useMemo(() => getProfessionFilterOptions(professions, tags), [professions, tags]);
  const managementOptions = useMemo(() => getManagementOptions(tags), [tags]);

  const toggleSet = <K extends 'assigned' | 'lastSeen' | 'status' | 'battery'>(
    group: K,
    key: keyof IAdminPanelFilters[K],
  ) => {
    onChange({
      ...filters,
      [group]: { ...filters[group], [key]: !filters[group][key] },
    });
  };

  const toggleList = (group: 'professions' | 'management', id: string) => {
    const current = filters[group];
    onChange({
      ...filters,
      [group]: current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    });
  };

  const hasActive =
    Object.values(filters.assigned).some(Boolean) ||
    Object.values(filters.lastSeen).some(Boolean) ||
    Object.values(filters.status).some(Boolean) ||
    Object.values(filters.battery).some(Boolean) ||
    filters.professions.length > 0 ||
    filters.management.length > 0;

  return (
    <ScrollArea className={styles.sidebar}>
      <aside className={styles.sidebarBody}>
        <div className={styles.sidebarHeader}>
          <span className={styles.sidebarTitle}>{t('adminTags.filters.title')}</span>
          {hasActive && (
            <button type="button" className={styles.clearBtn} onClick={onClear}>
              {t('adminTags.filters.clear')}
            </button>
          )}
        </div>

        <FilterGroup
          title={t('adminTags.filters.assigned')}
          rows={[
            { id: 'assigned', label: t('adminTags.filters.assignedYes'), checked: filters.assigned.assigned, onToggle: () => toggleSet('assigned', 'assigned') },
            { id: 'unassigned', label: t('adminTags.filters.assignedNo'), checked: filters.assigned.unassigned, onToggle: () => toggleSet('assigned', 'unassigned') },
          ]}
        />

        <FilterGroup
          title={t('adminTags.filters.status')}
          rows={(['Good', 'Sleeping', 'Storage', 'NoReception'] as const).map((s) => ({
            id: s,
            label: t(`adminTags.status.${s}`),
            checked: filters.status[s],
            onToggle: () => toggleSet('status', s),
          }))}
        />

        <FilterGroup
          title={t('adminTags.filters.battery')}
          rows={(['high', 'medium', 'low'] as const).map((b) => ({
            id: b,
            label: t(`adminTags.filters.battery_${b}`),
            checked: filters.battery[b],
            onToggle: () => toggleSet('battery', b),
          }))}
        />

        <FilterGroup
          title={t('adminTags.filters.lastSeen')}
          rows={(['ok', 'warning', 'alert'] as const).map((l) => ({
            id: l,
            label: t(`adminTags.filters.lastSeen_${l}`),
            checked: filters.lastSeen[l],
            onToggle: () => toggleSet('lastSeen', l),
          }))}
        />

        <FilterGroup
          title={t('adminTags.filters.professions')}
          rows={professionOptions
            // chip text = specialty when present, else the translated trade
            // (mirrors trusstor's ProfessionDisplayWithTradeIcon).
            .map((o) => ({ o, chipText: o.specialty || tradeLabel(t, o.tradeId) }))
            // sort by count desc, then chip text — same as trusstor's filter list.
            .sort((a, b) => b.o.count - a.o.count || a.chipText.localeCompare(b.chipText))
            .map(({ o, chipText }) => ({
              id: o.id,
              label: chipText,
              node: (
                <Tooltip content={o.contractor ? `${chipText} · ${o.contractor}` : chipText}>
                  <span className={styles.filterPill}>
                    <TradePill
                      label={chipText}
                      {...(o.backgroundColor ? { backgroundColor: o.backgroundColor } : {})}
                      tradeId={o.tradeId}
                    />
                    {o.contractor && <span className={styles.filterContractor}>{o.contractor}</span>}
                  </span>
                </Tooltip>
              ),
              count: o.count,
              checked: filters.professions.includes(o.id),
              onToggle: () => toggleList('professions', o.id),
            }))}
        />

        <FilterGroup
          title={t('adminTags.filters.management')}
          rows={managementOptions.map((o) => ({
            id: o.id,
            label: o.label,
            checked: filters.management.includes(o.id),
            onToggle: () => toggleList('management', o.id),
          }))}
        />
      </aside>
    </ScrollArea>
  );
}
